import { deleteField } from "firebase/firestore";
import { GroupIds } from "../models/enumerations/group-ids";
import { QuestionIds } from "../models/enumerations/question-ids";
import { MovementScore } from "../models/interfaces/scores/movement-score";
import { MskScore } from "../models/interfaces/scores/msk-score";
import AssessmentResponseService from "./services/assessment-response-service/assessment-response-service";
import MskScoreServiceAssessments from "./services/msk-score-service-assessments/msk-score-service-assessments";
import { PainArea, PainAreaToTranslationKey } from "../models/enumerations/pain-area";
import { FacingDirection, FacingDirectionToTranslationKey } from "../models/enumerations/facing-directions";
import i18n from "../i18n";
import { MskScoreVersion } from "../models/enumerations/msk-score-versions";
type ScoreSection = Record<string, any>;

export default class MskScoreUtil {
    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    public static async getLatest(
        userId: string
    ): Promise<MskScore | undefined> {
        const mskScores = await MskScoreServiceAssessments.getBy(
            // filter
            [
                {
                    field: "userId",
                    operator: "==",
                    value: userId,
                },
            ],
            // order
            [
                {
                    field: "created",
                    direction: "desc",
                },
            ],
            // limit
            1
        );

        if (mskScores == null || mskScores.length === 0) {
            return undefined;
        }

        return mskScores[0];
    }

    public static getNumberOfPainLocations(mskScore?: MskScore, assessmentResponses?: any) {
        if (!mskScore && !assessmentResponses) {
            return 0;
        }
        let numOfPainCount = 0;
        if (mskScore) {
            numOfPainCount = this.getNumberOfPainLocationsMovementAssessmentFromMsk(mskScore);
        }
        let numOfInjuryCount = 0;
        if (assessmentResponses) {
            numOfInjuryCount = this.getNumberOfPainLocationsInjuryHistoryAssessment(assessmentResponses);
        }

        return numOfPainCount + numOfInjuryCount;
    }

    public static getNumberOfPainLocationsMovementAssessmentFromMsk(mskScore: MskScore) {
        let numOfPainCount = 0;

        if (mskScore.movementScore?.balanceScore?.score === 0) {
            numOfPainCount++;
        }
        if (mskScore.movementScore?.hasShoulderClearingPain) {
            numOfPainCount++;
        }
        if (mskScore.movementScore?.hasSpineClearingPain) {
            numOfPainCount++;
        }
        if (mskScore.movementScore?.rotationScore?.score === 0) {
            numOfPainCount++;
        }
        if (mskScore.movementScore?.shoulderMobilityScore?.score === 0) {
            numOfPainCount++;
        }
        if (mskScore.movementScore?.squatScore?.score === 0) {
            numOfPainCount++;
        }
        if (mskScore.movementScore?.toeTouchScore?.score === 0) {
            numOfPainCount++;
        }

        return numOfPainCount;
    }

    public static getNumberOfPainLocationsMovementAssessment(assessmentResponses: any) {
        let numOfPainCount = 0;

        if (assessmentResponses) {
            const movementResponse: any = assessmentResponses!.find((assessmentResponse: any) => assessmentResponse[0].groupId === GroupIds.MOVEMENT_HEALTH)
            const movementAssessmentResponse = AssessmentResponseService.getMovementAssessmentResponses(movementResponse);

            if (movementAssessmentResponse.balanceQPain) {
                numOfPainCount++;
            }
            if (movementAssessmentResponse.shoulderClearingQPain) {
                numOfPainCount++;
            }
            if (movementAssessmentResponse.spineClearingQPain) {
                numOfPainCount++;
            }
            if (movementAssessmentResponse.rotationQPain) {
                numOfPainCount++;
            }
            if (movementAssessmentResponse.shoulderMobilityQPain) {
                numOfPainCount++;
            }
            if (movementAssessmentResponse.squatQPain) {
                numOfPainCount++;
            }
            if (movementAssessmentResponse.toeTouchQPain) {
                numOfPainCount++;
            }
        }

        return numOfPainCount;
    }

    public static getNumberOfPainLocationsInjuryHistoryAssessment(assessmentResponses: any) {
        let numOfInjuryCount = 0;

        const injuryHistory = assessmentResponses.find((response: any) => response[0].groupId === GroupIds.INJURY_HISTORY)
        if (injuryHistory) {
            const response = injuryHistory[0].responses.find((response: any) => response.questionId === QuestionIds.INJURY_HISTORY_PAIN_AREAS);
            if (response) {
                let temp = response.answerResponse.split(",").length;
                numOfInjuryCount += temp;
            }
        }

        return numOfInjuryCount;
    }
    public static baseCheckMskScore = (latestMskData?: MskScore): MskScoreVersion => {
        switch (latestMskData?.version) {
            case undefined: // No msk scored will have V1 attached, we have logic to distinguish between V1 and V0 throughout. We could write a script to add v1 to each valid mskScore or keep this logic. Keeping for now
                return this.checkMskScoreV1(latestMskData) ? MskScoreVersion.V1 : MskScoreVersion.V0;
            case MskScoreVersion.V2:
                return this.checkMskScoreV2(latestMskData) ? MskScoreVersion.V2 : MskScoreVersion.V0;
            default:
                return MskScoreVersion.V0;
        }
    }

    public static checkMskScoreV1 = (latestMskData?: MskScore): boolean => {
        if (!latestMskData) return false;

        const { movementScore, lifestyleScore } = latestMskData;

        // Utility function to check if all specified keys exist in an object
        const hasKeys = (obj: ScoreSection | undefined, keys: string[]): boolean =>
            keys.every(key => obj?.hasOwnProperty(key));

        // Check movementScore sections
        const toeTouchValid = hasKeys(movementScore?.toeTouchScore, [
            'frontToeLeft', 'backToeLeft', 'frontToeRight', 'backToeRight', 'painSeverity'
        ]);

        const shoulderMobilityValid = hasKeys(movementScore?.shoulderMobilityScore, [
            'paperPassLeft', 'paperPassRight', 'fingerTipsLeft', 'fingerTipsRight', 'painSeverity'
        ]);

        const rotationValid = hasKeys(movementScore?.rotationScore, [
            'feetTogetherLeft', 'feetTogetherRight', 'rightFootBackLeft', 'rightFootBackRight', 'painSeverity'
        ]);

        const squatValid = hasKeys(movementScore?.squatScore, [
            'squat', 'fingerTips', 'fists', 'painSeverity'
        ]);

        const balanceValid = hasKeys(movementScore?.balanceScore, [
            'oneFootRight', 'oneFootLeft', 'painSeverity'
        ]) &&
            hasKeys(movementScore?.balanceScore?.twoFeetLeft, ['attempts', 'success']) &&
            hasKeys(movementScore?.balanceScore?.twoFeetRight, ['attempts', 'success']) &&
            hasKeys(movementScore?.balanceScore?.threeFeetLeft, ['attempts', 'success']) &&
            hasKeys(movementScore?.balanceScore?.threeFeetRight, ['attempts', 'success']);

        const clearingPainValid = hasKeys(movementScore, [
            'shoulderClearingPainSeverity', 'spineClearingPainSeverity'
        ]);

        // Check lifestyleScore sections
        const bodyCompositionValid = hasKeys(lifestyleScore?.bodyCompositionScore, ['bmi']);
        const breathingValid = hasKeys(lifestyleScore?.breathingScore, [
            'breathHoldTime', 'breathingQuestionnaireScore', 'isSmoking'
        ]);
        const injuryHistoryValid = hasKeys(lifestyleScore?.injuryHistoryScore, [
            'previousInjury', 'currentPain', 'seenPhysician', 'recoveryScore'
        ]);
        const sleepValid = hasKeys(lifestyleScore?.sleepScore, ['psqiScore']);

        return (
            !!movementScore &&
            !!lifestyleScore &&
            toeTouchValid &&
            shoulderMobilityValid &&
            rotationValid &&
            squatValid &&
            balanceValid &&
            clearingPainValid &&
            bodyCompositionValid &&
            breathingValid &&
            injuryHistoryValid &&
            sleepValid
        );
    };

    public static checkMskScoreV2 = (latestMskData?: MskScore): boolean => {
        if (!latestMskData) return false;

        const { movementScore, lifestyleScore } = latestMskData;

        const hasKeys = (obj: any, keys: string[]): boolean =>
            keys.every(key => obj?.hasOwnProperty(key));

        const hasPainKey = (obj: any): boolean =>
            obj?.hasOwnProperty("painSeverity") ||
            obj?.hasOwnProperty("painAreasAndScales");

        const hasClearingPainKey = (obj: any, base: "shoulder" | "spine"): boolean => {
            const severityKey = `${base}ClearingPainSeverity`;
            const areaKey = `${base}ClearingPainAreaAndScale`;
            return obj?.hasOwnProperty(severityKey) || obj?.hasOwnProperty(areaKey);
        };

        // if they are v2 and no movementscore percentage, they haven't done movement portion yet.
        // don't check movement fields
        let toeTouchValid;
        let shoulderMobilityValid;
        let rotationValid;
        let squatValid;
        let balanceValid;
        let clearingPainValid;
        if (movementScore && movementScore.percentage === null) {
            toeTouchValid = true;
            shoulderMobilityValid = true;
            rotationValid = true;
            squatValid = true;
            balanceValid = true;
            clearingPainValid = true;
        }
        else {
            // Movement Score Checks
            toeTouchValid =
                hasKeys(movementScore?.toeTouchScore, [
                    "frontToeLeft",
                    "backToeLeft",
                    "frontToeRight",
                    "backToeRight"
                ]) && hasPainKey(movementScore?.toeTouchScore);

            shoulderMobilityValid =
                hasKeys(movementScore?.shoulderMobilityScore, [
                    "paperPassLeft",
                    "paperPassRight",
                    "fingerTipsLeft",
                    "fingerTipsRight"
                ]) && hasPainKey(movementScore?.shoulderMobilityScore);

            rotationValid =
                hasKeys(movementScore?.rotationScore, [
                    "feetTogetherLeft",
                    "feetTogetherRight",
                    "rightFootBackLeft",
                    "rightFootBackRight"
                ]) && hasPainKey(movementScore?.rotationScore);

            squatValid =
                hasKeys(movementScore?.squatScore, ["squat", "fingerTips", "fists"]) &&
                hasPainKey(movementScore?.squatScore);

            balanceValid =
                hasKeys(movementScore?.balanceScore, ["oneFootRight", "oneFootLeft"]) &&
                hasPainKey(movementScore?.balanceScore) &&
                hasKeys(movementScore?.balanceScore?.twoFeetLeft, ["attempts", "success"]) &&
                hasKeys(movementScore?.balanceScore?.twoFeetRight, ["attempts", "success"]) &&
                hasKeys(movementScore?.balanceScore?.threeFeetLeft, ["attempts", "success"]) &&
                hasKeys(movementScore?.balanceScore?.threeFeetRight, ["attempts", "success"]);

            clearingPainValid =
                hasClearingPainKey(movementScore, "shoulder") &&
                hasClearingPainKey(movementScore, "spine");
        }

        let bodyCompositionValid;
        let breathingValid;
        let injuryHistoryValid;
        let sleepValid;
        // if lifestyle score percentage is null, they haven't done lifestyle portion yet.
        // ignore checkers
        if (lifestyleScore && lifestyleScore.percentage === null) {
            bodyCompositionValid = true;
            breathingValid = true;
            injuryHistoryValid = true;
            sleepValid = true;
        }
        else {
            // Lifestyle Score Checks
            bodyCompositionValid = hasKeys(lifestyleScore?.bodyCompositionScore, ["bmi"]);
            breathingValid = hasKeys(lifestyleScore?.breathingScore, [
                "breathHoldTime",
                "breathingQuestionnaireScore",
                "isSmoking"
            ]);
            injuryHistoryValid = hasKeys(lifestyleScore?.injuryHistoryScore, [
                "previousInjury",
                "currentPain",
                "seenPhysician",
                "recoveryScore"
            ]);
            sleepValid = hasKeys(lifestyleScore?.sleepScore, ["psqiScore"]);
        }


        return (
            !!movementScore &&
            !!lifestyleScore &&
            toeTouchValid &&
            shoulderMobilityValid &&
            rotationValid &&
            squatValid &&
            balanceValid &&
            clearingPainValid &&
            bodyCompositionValid &&
            breathingValid &&
            injuryHistoryValid &&
            sleepValid
        );
    };

    // Helper to normalize names like "Right Shoulder" -> "rightShoulder"
    public static toPainKey(name: string, direction?: string): string {
        const base = name.replace(/\s+/g, "").replace(/^./, (c) => c.toLowerCase());

        const isShoulder =
            base === "leftShoulder" ||
            base === "rightShoulder";

        if (!isShoulder || !direction) {
            return base;
        }

        if (direction === FacingDirection.FRONT) {
            return `front${base.charAt(0).toUpperCase()}${base.slice(1)}`;
        }

        if (direction === FacingDirection.BACK) {
            return `back${base.charAt(0).toUpperCase()}${base.slice(1)}`;
        }

        return base;
    }


    /**
  * Retires all legacy painSeverity fields.
  * Safe against schema refactors (compile-time verified).
  */
    public static retireMovementPainSeverity(mskScore: MskScore): void {
        const movement = mskScore.movementScore;
        if (!movement) return;

        const groupKeys: (keyof MovementScore)[] = [
            "toeTouchScore",
            "shoulderMobilityScore",
            "squatScore",
            "balanceScore",
            "rotationScore",
        ];

        // delete inside each movement group
        for (const key of groupKeys) {
            const group = movement[key];
            if (group && typeof group === "object" && "painAreasAndScales" in group) {
                (group as any).painSeverity = deleteField();
            }
        }

        // directly mark on the top-level movementScore
        movement.shoulderClearingPainSeverity = deleteField() as any;
        movement.spineClearingPainSeverity = deleteField() as any;
    }

    public static retireLifestyleInjuryHistoryPainLocations(mskScore: MskScore): void {
        mskScore.lifestyleScore?.injuryHistoryScore &&
            (mskScore.lifestyleScore.injuryHistoryScore.currentPainLocations = deleteField() as any);
    }

    public static getPainHeaderText(
        area: string,
        direction: string
    ): string {
        // Safely cast incoming strings to the enum value type
        const areaEnum = area as PainArea;
        const directionEnum = direction as FacingDirection;

        const areaKey = PainAreaToTranslationKey[areaEnum];
        const directionKey = FacingDirectionToTranslationKey[directionEnum];

        if (!areaKey) {
            // Fallback if translation not found
            return area;
        }

        const areaLabel = i18n.t(areaKey);

        // Only FRONT/BACK have translation entries
        const directionLabel = directionKey ? i18n.t(directionKey) : "";

        return directionLabel
            ? `${areaLabel} (${directionLabel})`
            : areaLabel;
    }

    public static isLow(groupId: string, percent?: number, pass?: boolean): boolean {
        if (percent) {
            switch (groupId) {
                // From https://docs.google.com/spreadsheets/d/14DSKCtdkPA4eeBhkTjZy-4aDrfP_ozjPU48oyIR9IU0/edit#gid=449420902
                case GroupIds.TOE_TOUCH:
                    return percent < 75;
                case GroupIds.SHOULDER_MOBILITY:
                    return percent <= 65;
                case GroupIds.ROTATION:
                    return percent <= 69;
                case GroupIds.SQUAT:
                    return percent <= 49;
                case GroupIds.BALANCE:
                    return percent <= 69;
                case GroupIds.MOVEMENT_HEALTH:
                    return percent <= 65;
                default:
                    return false;
            }
        }
        else if (pass) {
            switch (groupId) {
                // From https://docs.google.com/spreadsheets/d/14DSKCtdkPA4eeBhkTjZy-4aDrfP_ozjPU48oyIR9IU0/edit#gid=449420902
                case GroupIds.SHOULDER_CLEARING:
                    return pass;
                case GroupIds.SPINE_CLEARING:
                    return pass;
                default:
                    return false;
            }
        }
        return false;
    }


    // #endregion Public Methods

    // -----------------------------------------------------------------------------------------
    // #region Private Methods
    // -----------------------------------------------------------------------------------------

    // #endregion Private Methods
}
